import { useState } from "react";
import { nf } from "../kb/shared";
import { vizTokens, breaksFor, SimpleTable } from "../kb/Charts";

// ATT&CK heatmap: one column per tactic (kill-chain order), techniques shaded by how
// many groups/software use them. One sequential hue; every cell is labelled with its
// value, so colour never carries the number alone. Table view for the full list.

function luminance(hex) {
  const h = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map(i => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function TechniqueMatrix({ data, ui, perColumn = 12 }) {
  const { B, fs } = ui;
  const t = vizTokens(B);
  const [showAll, setShowAll] = useState(false);
  const [table, setTable] = useState(false);

  const max = Math.max(1, ...data.rows.map(r => r.value));
  // Small counts (a focus of a few actors) get one step per value; larger ones get log breaks.
  const breaks = max <= t.ramp.length ? Array.from({ length: max }, (_, i) => i + 1) : breaksFor(max);
  const stepOf = (v) => {
    let b = 0;
    for (let i = 0; i < breaks.length; i++) if (v >= breaks[i]) b = i;
    return Math.round(b * (t.ramp.length - 1) / Math.max(1, breaks.length - 1));
  };
  // On a dark surface the ramp's dark end is "low"; keep that ordering (more = more contrast).
  const fillOf = (v) => t.ramp[stepOf(v)];
  const inkOn = (hex) => (luminance(hex) > 0.35 ? "#0b0b0b" : "#ffffff");

  const columns = (data.tactics || []).map(tac => ({
    tac, items: data.rows.filter(r => (r.tactics || []).includes(tac)).sort((a, b) => b.value - a.value || a.label.localeCompare(b.label)),
  })).filter(c => c.items.length);

  const btn = { background: "none", border: `1px solid ${B.border}`, borderRadius: 6, color: B.textMuted, cursor: "pointer", fontSize: fs(11), padding: "3px 9px", fontFamily: "'Satoshi', sans-serif" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: fs(11), color: t.inkDim }}>{data.measureLabel}</span>
        {breaks.map((b, i) => (
          <span key={b} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 14, height: 10, borderRadius: 2, background: t.ramp[Math.round(i * (t.ramp.length - 1) / Math.max(1, breaks.length - 1))] }} />
            <span style={{ fontSize: fs(11), color: t.inkMuted, fontVariantNumeric: "tabular-nums" }}>{nf(b)}{i === breaks.length - 1 ? (breaks.length > 1 && max > b ? "+" : "") : breaks[i + 1] - 1 > b ? `–${nf(breaks[i + 1] - 1)}` : ""}</span>
          </span>
        ))}
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {!table && <button data-noprint onClick={() => setShowAll(s => !s)} style={btn}>{showAll ? `Top ${perColumn} per tactic` : "Show every technique"}</button>}
          <button data-noprint onClick={() => setTable(x => !x)} style={btn}>{table ? "Show heatmap" : "Show table"}</button>
        </span>
      </div>

      {table ? (
        <SimpleTable head={["Technique", data.measureLabel]} rows={[...data.rows].sort((a, b) => b.value - a.value).map(r => [`${r.label}${r.attackId ? ` (${r.attackId})` : ""}`, nf(r.value)])} ui={ui} />
      ) : (
        <div style={{ overflowX: "auto", paddingBottom: 6 }}>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, minmax(112px, 1fr))`, gap: 5, minWidth: columns.length * 117 }}>
            {columns.map(col => {
              const items = showAll ? col.items : col.items.slice(0, perColumn);
              return (
                <div key={col.tac} style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                  <div style={{ fontSize: fs(11), fontWeight: 800, color: B.text, padding: "2px 2px 6px", borderBottom: `2px solid ${t.axis}`, marginBottom: 3, minHeight: 30 }}>
                    {col.tac}
                    <div style={{ fontSize: fs(10), fontWeight: 500, color: B.textDim }}>{col.items.length} technique{col.items.length === 1 ? "" : "s"}</div>
                  </div>
                  {items.map(r => {
                    const bg = fillOf(r.value);
                    const ink = inkOn(bg);
                    return (
                      <a key={r.key} href={r.link ? `#/kb/e/${encodeURIComponent(r.link)}` : undefined}
                        title={`${r.label}${r.attackId ? ` (${r.attackId})` : ""}: ${nf(r.value)} ${data.measureLabel.toLowerCase()}`}
                        style={{ display: "flex", gap: 6, alignItems: "flex-start", background: bg, color: ink, borderRadius: 4, padding: "5px 6px", textDecoration: "none", fontSize: fs(11), lineHeight: 1.3 }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{r.label}</span>
                        <span style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{nf(r.value)}</span>
                      </a>
                    );
                  })}
                  {!showAll && col.items.length > perColumn && (
                    <button onClick={() => setShowAll(true)} style={{ ...btn, border: "none", padding: "4px 2px", textAlign: "left", color: B.accent }}>+{col.items.length - perColumn} more</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
